import React, { useEffect, useRef, useState } from 'react';
import { Play, Pause, Volume2, VolumeX, Maximize } from 'lucide-react';
import { absUrl } from '../lib/api';

/**
 * Ad video carousel.
 *
 * Round 4 changes:
 *  - Use `object-contain` instead of `object-cover` so the video is never
 *    cropped (logo remains visible even when aspect ratios don't match).
 *  - Progress bar is now touch/click responsive: tap or drag to seek.
 */
export default function AdVideoSection({ videos = [] }) {
    const [idx, setIdx] = useState(0);
    const videoRef = useRef(null);
    const progressTrackRef = useRef(null);
    const draggingRef = useRef(false);
    const [playing, setPlaying] = useState(true);
    const [muted, setMuted] = useState(true);
    const [progress, setProgress] = useState(0);
    const [dur, setDur] = useState(0);
    const [cur, setCur] = useState(0);

    const count = videos.length;
    const video = videos[idx];

    useEffect(() => {
        const v = videoRef.current; if (!v) return;
        v.load();
        v.play().catch(() => setPlaying(false));
        setPlaying(true); setProgress(0);
    }, [idx]);

    // Global drag listeners so users can drag outside the track when seeking.
    // Registered unconditionally to obey React rules-of-hooks (we no-op inside
    // when the video/track isn't mounted).
    useEffect(() => {
        const ratioFromClientX = (clientX) => {
            const el = progressTrackRef.current; if (!el) return 0;
            const rect = el.getBoundingClientRect();
            if (rect.width <= 0) return 0;
            return (clientX - rect.left) / rect.width;
        };
        const seekByRatio = (ratio) => {
            const v = videoRef.current; if (!v) return;
            if (!isFinite(v.duration)) return;
            const clamped = Math.max(0, Math.min(1, ratio));
            v.currentTime = clamped * v.duration;
        };
        const onMove = (e) => {
            if (!draggingRef.current) return;
            const cx = e.touches ? e.touches[0]?.clientX : e.clientX;
            if (cx != null) seekByRatio(ratioFromClientX(cx));
        };
        const onUp = () => { draggingRef.current = false; };
        window.addEventListener('mousemove', onMove);
        window.addEventListener('mouseup', onUp);
        window.addEventListener('touchmove', onMove, { passive: true });
        window.addEventListener('touchend', onUp);
        return () => {
            window.removeEventListener('mousemove', onMove);
            window.removeEventListener('mouseup', onUp);
            window.removeEventListener('touchmove', onMove);
            window.removeEventListener('touchend', onUp);
        };
    }, []);

    if (!count) return null;

    const togglePlay = () => {
        const v = videoRef.current; if (!v) return;
        if (v.paused) { v.play(); setPlaying(true); } else { v.pause(); setPlaying(false); }
    };

    const onTime = () => {
        const v = videoRef.current; if (!v) return;
        setCur(v.currentTime); setDur(v.duration || 0);
        setProgress(v.duration ? (v.currentTime / v.duration) * 100 : 0);
    };

    const onEnded = () => setIdx((idx + 1) % count);

    const fmt = (s) => {
        if (!isFinite(s)) return '0:00';
        const m = Math.floor(s / 60); const sec = Math.floor(s % 60).toString().padStart(2, '0');
        return `${m}:${sec}`;
    };

    const enterFullscreen = () => {
        const v = videoRef.current; if (!v) return;
        if (v.requestFullscreen) v.requestFullscreen();
        else if (v.webkitEnterFullscreen) v.webkitEnterFullscreen();
    };

    // Seek by ratio (0..1) from a pointer position on the progress track.
    const seekByRatio = (ratio) => {
        const v = videoRef.current; if (!v) return;
        if (!isFinite(v.duration)) return;
        const clamped = Math.max(0, Math.min(1, ratio));
        v.currentTime = clamped * v.duration;
        onTime();
    };

    const ratioFromClientX = (clientX) => {
        const el = progressTrackRef.current; if (!el) return 0;
        const rect = el.getBoundingClientRect();
        if (rect.width <= 0) return 0;
        return (clientX - rect.left) / rect.width;
    };

    const startDrag = (clientX) => {
        draggingRef.current = true;
        seekByRatio(ratioFromClientX(clientX));
    };

    return (
        <div className="px-3 sm:px-4 mt-4">
            <div className="max-w-6xl mx-auto">
                <div className="relative rounded-2xl overflow-hidden bg-black shadow-soft">
                    <video
                        ref={videoRef}
                        src={absUrl(video.video_url)}
                        muted={muted}
                        autoPlay
                        playsInline
                        controlsList="nodownload noremoteplayback"
                        disableRemotePlayback
                        onContextMenu={(e) => e.preventDefault()}
                        onTimeUpdate={onTime}
                        onLoadedMetadata={onTime}
                        onEnded={onEnded}
                        className="w-full aspect-video object-contain bg-black"
                        data-testid="ad-video"
                    />
                    <button aria-label="Mute" onClick={() => setMuted(m => !m)} className="absolute top-3 right-3 bg-black/60 text-white p-2 rounded-full">
                        {muted ? <VolumeX size={16} /> : <Volume2 size={16} />}
                    </button>
                    {/* Progress bar — touch / mouse responsive. Enlarged hit-area for touch. */}
                    <div className="absolute left-0 right-0 bottom-11 px-3">
                        <div
                            ref={progressTrackRef}
                            className="relative h-4 flex items-center cursor-pointer touch-none"
                            role="slider"
                            aria-label="Seek video"
                            aria-valuemin={0}
                            aria-valuemax={100}
                            aria-valuenow={Math.round(progress)}
                            onMouseDown={(e) => startDrag(e.clientX)}
                            onTouchStart={(e) => startDrag(e.touches[0].clientX)}
                            data-testid="ad-video-progress-track"
                        >
                            <div className="h-1 w-full rounded-full bg-white/30 overflow-hidden">
                                <div
                                    className="h-full bg-sdf-red"
                                    style={{ width: `${progress}%` }}
                                    data-testid="ad-video-progress-fill"
                                />
                            </div>
                            {/* Thumb */}
                            <div
                                className="absolute -top-0.5 h-3 w-3 rounded-full bg-white shadow -translate-x-1/2"
                                style={{ left: `${progress}%` }}
                                aria-hidden
                            />
                        </div>
                    </div>
                    {/* Bottom controls */}
                    <div className="absolute left-0 right-0 bottom-2 px-3 flex items-center justify-between text-white text-xs">
                        <div className="flex items-center gap-2">
                            <button aria-label="Play/Pause" onClick={togglePlay} className="bg-black/60 p-1.5 rounded-full">
                                {playing ? <Pause size={14} /> : <Play size={14} />}
                            </button>
                            <span className="bg-black/50 px-2 py-0.5 rounded-full font-medium">{fmt(cur)} / {fmt(dur)}</span>
                        </div>
                        <button aria-label="Fullscreen" onClick={enterFullscreen} className="bg-black/60 p-1.5 rounded-full"><Maximize size={14} /></button>
                    </div>
                </div>
                {count > 1 && (
                    <div className="flex justify-center gap-1.5 mt-2">
                        {videos.map((_, i) => (
                            <button
                                key={i}
                                onClick={() => setIdx(i)}
                                aria-label={`Show ad ${i + 1}`}
                                className={`h-1.5 rounded-full transition-all ${i === idx ? 'w-6 bg-sdf-red' : 'w-2 bg-neutral-300'}`}
                            />
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}
